import type { ComponentProps, ElementType, ReactNode } from "react";
import { LuEllipsis as LuMoreHorizontal, LuX } from "react-icons/lu";

import {
  HorizontalItem,
  HorizontalItems,
  HorizontalItemsPopover,
  HorizontalItemsTrigger,
} from "@/components/shared/horizontal-items";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/lib";

// 40px + 8px gap
export const ACTION_BUTTON_SIZE = 48;

type EditorActionButtonProps<T extends ElementType> = {
  tooltipText?: string;
  action: T;
  active?: boolean;
  itemCount?: number;
  showTooltip?: boolean;
  className?: string;
  buttonClassName?: string;
} & ComponentProps<T>;

export const EditorFooterActionButton = <T extends ElementType>({
  tooltipText,
  action: Action,
  active = false,
  itemCount = 0,
  showTooltip = true,
  className,
  buttonClassName,
  ...props
}: EditorActionButtonProps<T>) => {
  const button = (
    <Action
      variant="outline"
      size="icon"
      className={cn(
        `
          relative size-10 rounded-full bg-background text-muted
          hover:border-primary hover:text-primary
        `,
        active ? "border-primary text-primary" : "",
        buttonClassName,
      )}
      {...props}
    />
  );

  return (
    <HorizontalItem>
      <div className={cn("editor-action-button relative", className)}>
        {showTooltip && (
          <InfoTooltip
            text={tooltipText}
            mobile={false}
            className="
              hidden
              md:block
            "
          >
            {button}
          </InfoTooltip>
        )}
        {!showTooltip && button}
        {itemCount > 0 && (
          <Badge
            className="
              absolute top-[-6px] right-[-6px] flex size-4 items-center
              justify-center p-0 text-center text-[11px] leading-none font-bold
            "
          >
            {itemCount}
          </Badge>
        )}
      </div>
    </HorizontalItem>
  );
};

export const EditorFooterActionContainer = ({
  children,
  className,
  size = ACTION_BUTTON_SIZE,
}: {
  children: ReactNode;
  className?: string;
  size?: number;
}) => {
  return (
    <HorizontalItems
      containerProps={{ className: cn("flex items-center gap-2", className) }}
      defaultSize={size}
      triggerSize={size}
    >
      {children}

      <HorizontalItemsTrigger>
        <div className="group relative">
          <Button
            variant="outline"
            size="icon"
            className="
              relative size-10 shrink-0 rounded-full bg-background text-muted
              group-data-[open=true]:border-primary
              group-data-[open=true]:bg-card group-data-[open=true]:text-primary
              hover:border-primary hover:text-primary
            "
          >
            <LuX
              className="
                hidden
                group-data-[open=true]:block
              "
            />
            <LuMoreHorizontal
              className="
                hidden
                group-data-[closed=true]:block
              "
            />
          </Button>
          <HorizontalItemsPopover
            className="my-2 -mr-2 flex flex-col gap-2 bg-card p-2"
            defaultSize={ACTION_BUTTON_SIZE}
          >
            {children}
          </HorizontalItemsPopover>
        </div>
      </HorizontalItemsTrigger>
    </HorizontalItems>
  );
};
